import { Link } from 'react-router-dom';

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 lg:p-12">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-slate-900 mb-4">Privacy Policy</h1>
            <p className="text-lg text-slate-600">
              How MicroFeedback protects and manages your data
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>

          {/* Overview */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-900 mb-6">Privacy by Design</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-blue-900">Security First</h3>
                  <p className="text-blue-700 mt-1">
                    MicroFeedback is built with privacy as a core principle. We collect only the minimum data necessary 
                    and implement multiple layers of security to protect your information.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Data Collection */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-900 mb-6">What Data We Collect</h2>
            
            <div className="space-y-6">
              <div className="border border-slate-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">
                  📊 Feedback Widget Data
                </h3>
                <p className="text-slate-600 mb-4">
                  When visitors interact with your feedback widget, we collect:
                </p>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span><strong>Vote:</strong> Whether the user clicked &quot;helpful&quot; (👍) or &quot;not helpful&quot; (👎)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span><strong>Page URL:</strong> The page where feedback was given</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span><strong>Optional Comment:</strong> Text feedback (only if user chooses to provide it)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span><strong>Anonymized Location:</strong> Country, region, city</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span><strong>IP Hash:</strong> Cryptographically hashed IP address (original IP never stored)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span><strong>Timestamp:</strong> When the feedback was submitted</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span><strong>Browser Info:</strong> User agent string for analytics</span>
                  </li>
                </ul>
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
                  <p className="text-green-800 text-sm">
                    <strong>Privacy Note:</strong> We do NOT collect names, email addresses, or any personally identifiable information from feedback users.
                  </p>
                </div>
              </div>

              <div className="border border-slate-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">
                  👤 Dashboard Account Data
                </h3>
                <p className="text-slate-600 mb-4">
                  For dashboard users (website owners), we store:
                </p>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span><strong>Email Address:</strong> Your login email</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span><strong>Site Information:</strong> Domain names and settings for your websites</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span><strong>Plan Details:</strong> Your subscription plan and billing information</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span><strong>Usage Data:</strong> Analytics about your feedback widget performance</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Data Security */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-900 mb-6">How We Protect Your Data</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-slate-200 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">Data Encryption</h3>
                </div>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li>• <strong>IP Address Hashing:</strong> All IP addresses are immediately hashed using SHA-256 with salt</li>
                  <li>• <strong>HTTPS Everywhere:</strong> All data transmission encrypted in transit</li>
                  <li>• <strong>HMAC Signing:</strong> Widget requests cryptographically signed</li>
                </ul>
              </div>

              <div className="border border-slate-200 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">Data Minimization</h3>
                </div>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li>• <strong>No Tracking:</strong> No cookies or tracking across websites</li>
                  <li>• <strong>No PII:</strong> No personal information collected from feedback users</li>
                  <li>• <strong>IP Anonymization:</strong> Original IP addresses never stored</li>
                  <li>• <strong>Purpose Limitation:</strong> Data used only for feedback analytics</li>
                </ul>
              </div>
            </div>
          </section>

          {/* GDPR Rights */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-900 mb-6">Your Data Rights (GDPR Compliant)</h2>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-green-900">GDPR Compliant</h3>
                  <p className="text-green-700 mt-1">
                    MicroFeedback provides comprehensive data rights and automated tools for GDPR compliance.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="border border-slate-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center">
                  <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Right to Data Access & Export
                </h3>
                <p className="text-slate-600 mb-4">
                  You can access and export all your data at any time through our dashboard.
                </p>
                <div className="bg-slate-50 rounded p-4">
                  <h4 className="font-medium text-slate-900 mb-2">Available Export Options:</h4>
                  <ul className="space-y-1 text-slate-600 text-sm">
                    <li>• <strong>CSV Export:</strong> All feedback data (votes, comments, analytics)</li>
                    <li>• <strong>Date Range Selection:</strong> Export data for any period (up to 365 days)</li>
                    <li>• <strong>Complete Export:</strong> Up to 100,000 records per export</li>
                    <li>• <strong>Secure Processing:</strong> Files generated on-demand, no persistent storage</li>
                  </ul>
                  <p className="text-sm text-slate-500 mt-2">
                    <strong>How to export:</strong> Go to Dashboard → Select Site → Export Data button
                  </p>
                </div>
              </div>

              <div className="border border-slate-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center">
                  <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Right to Data Deletion
                </h3>
                <p className="text-slate-600 mb-4">
                  You can delete your data completely through our automated deletion tools.
                </p>
                <div className="bg-slate-50 rounded p-4">
                  <h4 className="font-medium text-slate-900 mb-2">Deletion Options:</h4>
                  <ul className="space-y-2 text-slate-600 text-sm">
                    <li>• <strong>Delete Individual Sites:</strong> Remove a specific website and all its feedback data</li>
                    <li>• <strong>Delete Entire Account:</strong> Complete removal of your account and all associated data</li>
                 </ul>
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded">
                    <p className="text-amber-800 text-sm">
                      <strong>Complete Deletion Includes:</strong>
                    </p>
                    <ul className="text-amber-700 text-sm mt-1 space-y-1">
                      <li>• All votes and comments from your sites</li>
                      <li>• Analytics and aggregated data</li>
                      <li>• Site configuration and settings</li>
                      <li>• User account and profile information</li>
                    </ul>
                  </div>
                  <p className="text-sm text-slate-500 mt-2">
                    <strong>How to delete:</strong> Dashboard → Settings → Account Deletion
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Data Retention */}
          {/* FIXME: 
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-900 mb-6">Data Retention Policy</h2>
            
            <div className="space-y-4">
              <div className="border border-slate-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Feedback Data</h3>
                <ul className="space-y-2 text-slate-600">
                  <li>• <strong>Active Sites:</strong> Data retained as long as your account is active</li>
                  <li>• <strong>Deleted Sites:</strong> All data permanently deleted within 24 hours</li>
                  <li>• <strong>Deleted Accounts:</strong> Complete data erasure within 24 hours</li>
                </ul>
              </div>

              <div className="border border-slate-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Account Data</h3>
                <ul className="space-y-2 text-slate-600">
                  <li>• <strong>Profile Information:</strong> Retained while account is active</li>
                  <li>• <strong>Billing Records:</strong> Kept for 7 years as required by law</li>
                  <li>• <strong>Audit Logs:</strong> Security logs retained for 1 year</li>
                  <li>• <strong>Support Communications:</strong> Deleted after 2 years</li>
                </ul>
              </div>
            </div>
          </section>
          */}

          {/* Legal & Compliance */}
          {/* FIXME: 
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-900 mb-6">Legal Basis & Compliance</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-slate-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Legal Basis for Processing</h3>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li>• <strong>Dashboard Users:</strong> Contractual necessity for service provision</li>
                  <li>• <strong>Feedback Data:</strong> Legitimate interest in providing analytics</li>
                  <li>• <strong>Billing Data:</strong> Legal obligation for tax and accounting</li>
                  <li>• <strong>Marketing:</strong> Consent (easily withdrawn)</li>
                </ul>
              </div>

              <div className="border border-slate-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Compliance Standards</h3>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li>• <strong>GDPR:</strong> Full compliance with EU data protection laws</li>
                  <li>• <strong>CCPA:</strong> California privacy rights respected</li>
                  <li>• <strong>SOC 2:</strong> Google Cloud infrastructure compliance</li>
                  <li>• <strong>ISO 27001:</strong> Information security management</li>
                </ul>
              </div>
            </div>
          </section>
          */}

          {/* Third Party Services */}
          {/* FIXME: 
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-900 mb-6">Third-Party Services</h2>
            
            <div className="space-y-4">
              <div className="border border-slate-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Service Providers We Use</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">Infrastructure & Security</h4>
                    <ul className="space-y-1 text-slate-600 text-sm">
                      <li>• <strong>Google Cloud/Firebase:</strong> Database, authentication, functions</li>
                      <li>• <strong>Cloudflare:</strong> CDN, DDoS protection, edge computing</li>
                      <li>• <strong>Stripe:</strong> Payment processing and billing</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">Communications</h4>
                    <ul className="space-y-1 text-slate-600 text-sm">
                      <li>• <strong>SendGrid:</strong> Transactional emails and alerts</li>
                      <li>• <strong>GitHub:</strong> Code hosting and CI/CD</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-blue-800 text-sm">
                    <strong>Data Processing Agreements:</strong> All service providers have signed Data Processing Agreements (DPAs) 
                    and are GDPR compliant. Your data is only processed according to our instructions.
                  </p>
                </div>
              </div>
            </div>
          </section>
          */}
          {/* Contact & Updates */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-6">Contact & Updates</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-slate-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Privacy Questions</h3>
                <p className="text-slate-600 mb-4">
                  Have questions about your data or privacy? We&apos;re here to help.
                </p>
                <div className="space-y-2 text-sm">
                  <p><strong>Email:</strong> <a href="mailto:privacy@microfb.com" className="text-blue-600 hover:text-blue-800">privacy@microfb.com</a></p>
                  <p><strong>Response Time:</strong> Within 72 hours</p>
                  {/* <p><strong>Data Protection Officer:</strong> Available for EU residents</p> */}
                </div>
              </div>

              <div className="border border-slate-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Policy Updates</h3>
                <p className="text-slate-600 mb-4">
                  We&apos;ll notify you of any significant changes to this privacy policy.
                </p>
                <div className="space-y-2 text-sm text-slate-600">
                  {/* FIXME: 
                  <p>• Email notification for material changes</p>
                  <p>• 30-day notice period before changes take effect</p>
                  <p>• Version history available upon request</p>
                  */}
                  <p>• Changes will be announced in the privacy policy page</p>
                </div>
              </div>
            </div>
          </section>

          {/* Action Buttons */}
          <div className="border-t border-slate-200 pt-8">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/dashboard"
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-center"
              >
                Go to Dashboard
              </Link>
              {/* FIXME: 
              <Link
                to="/dashboard/settings"
                className="border border-slate-300 text-slate-700 px-6 py-3 rounded-lg font-semibold hover:bg-slate-50 transition-colors text-center"
              >
                Manage Data Settings
              </Link>
              */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
